type AddressLike = {
  address?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  uf?: string | null;
  number?: string | number | null;
  complement?: string | null;
  floor?: string | number | null;
} | null | undefined;

type PropertyLike = {
  parent_property_id?: number | string | null;
  parentPropertyId?: number | string | null;
} | null | undefined;

type AddressFormatOptions = {
  /**
   * The street number identifies the building, not the rented unit, so pages
   * that need a precise location (property details, map geocoding) may show it.
   */
  includeStreetNumber?: boolean;
};

/**
 * Public marketplace must never expose unit identifiers
 * (complement, floor/apartment number).
 */
export function formatStreetLine(
  address: AddressLike,
  { includeStreetNumber = false }: AddressFormatOptions = {}
): string {
  if (!address) {
    return "";
  }

  const street = String(address.address ?? "").trim();
  const streetNumber = String(address.number ?? "").trim();

  if (!includeStreetNumber || !street || !streetNumber) {
    return street;
  }

  return `${street}, ${streetNumber}`;
}

export function formatPublicPropertyAddress(
  address: AddressLike,
  options: AddressFormatOptions = {}
): string {
  if (!address) {
    return "";
  }

  const street = formatStreetLine(address, options);
  const neighborhood = String(address.neighborhood ?? "").trim();
  const city = String(address.city ?? "").trim();
  const uf = String(address.uf ?? "").trim();

  const locality = [neighborhood, city].filter(Boolean).join(", ");
  const region = uf ? (locality ? `${locality} - ${uf}` : uf) : locality;

  return [street, region].filter(Boolean).join(" — ");
}

export function isParentProperty(property: PropertyLike): boolean {
  if (!property) {
    return false;
  }

  const parentId = property.parent_property_id ?? property.parentPropertyId;
  if (parentId == null || parentId === "") {
    return true;
  }

  const parsed = Number(parentId);
  return !Number.isFinite(parsed) || parsed <= 0;
}

export function filterParentProperties<T extends PropertyLike>(
  properties: T[]
): T[] {
  return properties.filter((property) => isParentProperty(property));
}
